/**
 * Module dependencies.
 */

import { ComponentProps } from 'react';
import { FieldValues, UseControllerProps, useController } from 'react-hook-form';
import { assignRefs } from 'src/core/utils/refs';

import { Checkbox, CheckboxProps } from './index';

/**
 * `CheckboxFieldProps` type.
 */

type CheckboxFieldProps<T extends FieldValues> = {
  ref?: ComponentProps<typeof Checkbox>['ref'];
} & CheckboxProps &
  Pick<UseControllerProps<T>, 'control' | 'defaultValue' | 'name' | 'rules'>;

/**
 * Export `CheckboxField` component.
 */

export function CheckboxField<T extends FieldValues>(props: CheckboxFieldProps<T>) {
  const {
    control,
    defaultValue = false as CheckboxFieldProps<T>['defaultValue'],
    name,
    ref: forwardedRef,
    rules,
    ...rest
  } = props;

  const {
    field: { onBlur, onChange, ref, value },
    fieldState: { error }
  } = useController({
    control,
    defaultValue,
    name,
    rules
  });

  return (
    <Checkbox
      {...rest}
      error={error?.message}
      onBlur={onBlur}
      onChange={onChange}
      ref={assignRefs(forwardedRef, ref)}
      value={value}
    />
  );
}
